// frontend/src/pages/admin/AdminBookingForm.jsx
// ============================================
// ADMIN/RECEPTIONIST BOOKING FORM - Multi-step like Booking.jsx
// ============================================

import React, { useState, useEffect, useCallback } from "react";
import { fetchAllPackages } from "../../constants/packages";
import * as adminApi from '../../services/admin';
import {
  getPriceFromPackage,
  getExtraGuestCharge,
  getDownpaymentAmount,
  getMinCapacityFromPackage,
  getTotalMaxCapacity,
} from "../../config/packageData";
import "./AdminBookingForm.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

const isValidGmailAddress = (value) => /^[^\s@]+@gmail\.com$/i.test((value || "").trim());

const normalizeContactNumber = (value) => {
  const digitsOnly = (value || "").replace(/\D/g, "");
  if (!digitsOnly) return "+639";

  let subscriber = digitsOnly;
  if (subscriber.startsWith("639")) {
    subscriber = subscriber.slice(3);
  } else if (subscriber.startsWith("09")) {
    subscriber = subscriber.slice(2);
  }
  subscriber = subscriber.slice(0, 9);
  return `+639${subscriber}`;
};

function AdminBookingForm({ onClose, onBookingCreated, editingBooking }) {
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [addOns, setAddOns] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOasis, setSelectedOasis] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSession, setSelectedSession] = useState("");
  const [sessionData, setSessionData] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  const isSessionActive = (session) => {
    if (!sessionData || sessionData.length === 0) return true;
    return sessionData.some((s) => s.name === session && s.isActive);
  };

  const shouldDisableSessionOption = (session) => {
    return !isSessionActive(session);
  };

  // Helper to show modal alerts
  const showAlert = (title, message) => {
    setAlertModal({ isOpen: true, title, message });
  };

  const [formData, setFormData] = useState({
    customerName: "",
    customerContact: "+639",
    customerEmail: "",
    guestCount: 1,
    reservationDate: "",
    specialRequests: "",
    paymentMethod: "GCash",
    paymentType: "downpayment",
  });

  const [errors, setErrors] = useState({});
  const [bookedSessions, setBookedSessions] = useState({});

  // Fetch packages based on selected oasis
  const fetchPackagesForOasis = useCallback(async (oasis) => {
    if (!oasis) {
      setPackages([]);
      return [];
    }

    setLoadingPackages(true);
    try {
      const data = await fetchAllPackages();
      const filteredPackages = oasis === "Oasis 1"
        ? (data.Oasis1Packages || [])
        : (data.Oasis2Packages || []);
      setPackages(filteredPackages);
      return filteredPackages;
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackages([]);
      return [];
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  // Reset form state for new booking
  const resetFormState = useCallback(() => {
    setSelectedOasis("");
    setSelectedPackage(null);
    setSelectedSession("");
    setSelectedAddOns({});
    setFormData({
      customerName: "",
      customerContact: "+639",
      customerEmail: "",
      guestCount: 1,
      reservationDate: "",
      specialRequests: "",
      paymentMethod: "GCash",
      paymentType: "downpayment",
    });
    setErrors({});
    setStep(1);
  }, []);

  // Fetch sessions on mount and add-ons when session changes
  useEffect(() => {
    const loadData = async () => {
      try {
        const sessionResponse = await fetch(`${API_BASE_URL}/api/admin/sessions`);
        if (sessionResponse.ok) {
          const data = await sessionResponse.json();
          setSessionData(data);
        }
      } catch (error) {
        console.error("Error fetching session data:", error);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const loadAddOns = async () => {
      setLoadingAddOns(true);
      try {
        const url = selectedSession
          ? `${API_BASE_URL}/api/admin/addons/active?session=${encodeURIComponent(selectedSession)}`
          : `${API_BASE_URL}/api/admin/addons/active`;

        const addonsResponse = await fetch(url);
        if (addonsResponse.ok) {
          const data = await addonsResponse.json();
          setAddOns(data);

          const validAddonNames = new Set(data.map((addon) => addon.name));
          const filteredSelected = Object.fromEntries(
            Object.entries(selectedAddOns).filter(([name]) => validAddonNames.has(name))
          );
          if (Object.keys(filteredSelected).length !== Object.keys(selectedAddOns).length) {
            setSelectedAddOns(filteredSelected);
          }
        } else {
          setAddOns([]);
        }
      } catch (error) {
        console.error("Error fetching add-ons:", error);
        setAddOns([]);
      } finally {
        setLoadingAddOns(false);
      }
    };

    loadAddOns();
  }, [selectedSession]);

  // Reset form when not editing (new booking)
  useEffect(() => {
    if (!editingBooking) {
      resetFormState();
    }
  }, [editingBooking, resetFormState]);

  // Initialize with editing booking if provided
  useEffect(() => {
    if (editingBooking) {
      setSelectedOasis(editingBooking.oasis);
      setFormData({
        customerName: editingBooking.customerName || "",
        customerContact: normalizeContactNumber(editingBooking.customerContact || "+639"),
        customerEmail: editingBooking.customerEmail || "",
        guestCount: editingBooking.pax || 1,
        reservationDate: editingBooking.bookingDate || "",
        specialRequests: editingBooking.specialRequests || "",
        paymentMethod: editingBooking.paymentMethod || "GCash",
        paymentType: editingBooking.paymentType || "downpayment",
      });
      setStep(1);

      const restorePackage = async () => {
        const availablePackages = await fetchPackagesForOasis(editingBooking.oasis);
        const packageValue = editingBooking.package?.id || editingBooking.package;
        const matchingPackage = availablePackages.find(
          (pkg) => pkg.id === packageValue || pkg.name === packageValue,
        );
        setSelectedPackage(matchingPackage || null);
        setSelectedSession(editingBooking.session || "");
      };

      restorePackage();
    }
  }, [editingBooking, fetchPackagesForOasis]);

  // Get current package data
  const allowedSessionValues = ['Day', 'Night', '22hrs'];

  const currentPackage = selectedPackage ? {
    ...selectedPackage,
    sessions: (selectedPackage.sessions?.length > 0
      ? selectedPackage.sessions
      : selectedPackage.availableSessions || []
    )
      .filter((session) => allowedSessionValues.includes(session))
      .filter((session) => isSessionActive(session)),
  } : null;

  useEffect(() => {
    if (selectedSession && !isSessionActive(selectedSession)) {
      setSelectedSession("");
      setSelectedAddOns({});
    }
  }, [selectedSession, sessionData]);

  const getSessionDisplay = (session) => {
    if (!session) return session;
    const sessionMap = {
      Day: "Day (8AM - 5PM)",
      Night: "Night (6PM - 6AM)",
      "22hrs": "22-Hour Session (Whole Day)",
    };
    return sessionMap[session] || session;
  };

  // Get minimum date (tomorrow) - require 1 day advance booking
  const getMinimumDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  };

  const getMinimumBookingDateValue = () => formatDateToString(getMinimumDate());

  // Convert date to YYYY-MM-DD format
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch booked sessions for selected date
  useEffect(() => {
    if (!formData.reservationDate || !selectedOasis || !selectedPackage) {
      setBookedSessions({});
      return;
    }

    const fetchBookedSessions = async () => {
      try {
        const url = `${API_BASE_URL}/api/bookings/booked-dates?oasis=${encodeURIComponent(selectedOasis)}`;
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.bookedDates) {
            const dateStr = formData.reservationDate;
            const dateBookings = data.bookedDates[dateStr] || {};
            
            const bookedSessionData = {};
            if (dateBookings.Day?.booked) bookedSessionData.Day = true;
            if (dateBookings.Night?.booked) bookedSessionData.Night = true;
            if (dateBookings['22hrs']?.booked) bookedSessionData['22hrs'] = true;
            
            console.log('📅 Booked sessions for this date:', bookedSessionData);
            setBookedSessions(bookedSessionData);
          }
        }
      } catch (error) {
        console.error('Error fetching booked sessions:', error);
      }
    };

    fetchBookedSessions();
  }, [formData.reservationDate, selectedOasis, selectedPackage]);

  const getMaxCapacityForPackage = () => getTotalMaxCapacity(currentPackage);
  const getMinCapacityForPackage = () => getMinCapacityFromPackage(currentPackage);

  const calculatePrice = () => {
    if (!selectedSession || !formData.reservationDate || !currentPackage) {
      console.log("❌ Missing price calculation requirements:", {
        selectedSession,
        reservationDate: formData.reservationDate,
        currentPackage: currentPackage?.name
      });
      return 0;
    }
    const price = getPriceFromPackage(
      currentPackage,
      selectedSession,
      formData.reservationDate,
      formData.guestCount
    );
    console.log("📦 Base package price:", price, "for", currentPackage.name, selectedSession, formData.guestCount, "pax");
    return price;
  };

  const calculateExtraGuestCharges = () =>
    getExtraGuestCharge(currentPackage, formData.guestCount);

  const calculateAddonsTotal = () =>
    Object.values(selectedAddOns).reduce((sum, price) => sum + price, 0);

  const getTotalPrice = () => {
    const base = calculatePrice();
    const extraGuest = calculateExtraGuestCharges();
    const addons = calculateAddonsTotal();
    const total = base + extraGuest + addons;
    console.log("💰 Price Calculation:");
    console.log("   - base:", base);
    console.log("   - extraGuest:", extraGuest);
    console.log("   - addons:", addons);
    console.log("   - TOTAL:", total);
    return total;
  };

  const handleAddonToggle = (addon) => {
    const newSelected = { ...selectedAddOns };
    if (newSelected[addon.name]) {
      delete newSelected[addon.name];
    } else {
      newSelected[addon.name] = addon.price;
    }
    setSelectedAddOns(newSelected);
  };

  useEffect(() => {
    if (!selectedSession) {
      setSelectedAddOns({});
      return;
    }

    const validSelected = Object.fromEntries(
      Object.entries(selectedAddOns).filter(([name]) =>
        addOns.some((addon) => addon.name === name)
      )
    );

    if (Object.keys(validSelected).length !== Object.keys(selectedAddOns).length) {
      setSelectedAddOns(validSelected);
    }
  }, [selectedSession, addOns]);

  const getDownpayment = () => {
    const dp = getDownpaymentAmount(selectedSession, sessionData);
    console.log("💳 Downpayment calculated:", dp, "for session:", selectedSession);
    return dp;
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      const trimmedName = formData.customerName?.trim();
      const trimmedContact = formData.customerContact?.trim();
      const trimmedEmail = formData.customerEmail?.trim();

      if (!trimmedName) {
        newErrors.customerName = "Customer name is required";
      } else if (trimmedName.length < 2) {
        newErrors.customerName = "Customer name must be at least 2 characters";
      } else if (/\d/.test(trimmedName)) {
        newErrors.customerName = "Customer name cannot contain numbers";
      }

      if (!trimmedContact) {
        newErrors.customerContact = "Contact number is required";
      } else if (!/^\+639\d{9}$/.test(trimmedContact)) {
        newErrors.customerContact = "Contact number must start with +639 and include 9 digits";
      }

      if (!trimmedEmail) {
        newErrors.customerEmail = "Email is required";
      } else if (!isValidGmailAddress(trimmedEmail)) {
        newErrors.customerEmail = "Email must be a valid Gmail address ending with @gmail.com";
      }

      if (!selectedOasis) newErrors.oasis = "Location is required";
      if (!selectedPackage) newErrors.package = "Package is required";
    }

    if (stepNum === 2) {
      if (!formData.reservationDate) newErrors.reservationDate = "Reservation date is required";
      
      // Check if date is at least 1 day in advance
      if (formData.reservationDate) {
        const selectedDate = new Date(formData.reservationDate);
        const tomorrow = getMinimumDate();
        if (selectedDate < tomorrow) {
          newErrors.reservationDate = "Booking must be made at least 1 day in advance";
        }
      }
      
      if (!selectedSession) newErrors.session = "Session is required";
      
      // Check if the selected session is already booked
      if (selectedSession && bookedSessions[selectedSession]) {
        newErrors.session = "This session is already fully booked on this date";
      }
      
      if (!formData.guestCount || formData.guestCount < 1) newErrors.guestCount = "Guest count must be at least 1";

      const minCap = getMinCapacityForPackage();
      const maxCap = getMaxCapacityForPackage();
      if (minCap > 0 && formData.guestCount < minCap) {
        newErrors.guestCount = `Minimum ${minCap} guests required for this package`;
      }
      if (maxCap > 0 && formData.guestCount > maxCap) {
        newErrors.guestCount = `Guest count cannot exceed ${maxCap} for this package`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    // Additional safety checks before submission
    if (!selectedPackage) {
      showAlert("Validation Error", "Package is required");
      return;
    }
    
    if (!selectedSession) {
      showAlert("Validation Error", "Session is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const totalPrice = getTotalPrice();
      const downpayment = getDownpayment();

      console.log("\n🚀 SUBMISSION DEBUG INFO:");
      console.log("===============================");
      console.log("Form Data State:");
      console.log("  - customerName:", formData.customerName, typeof formData.customerName);
      console.log("  - customerEmail:", formData.customerEmail, typeof formData.customerEmail);
      console.log("  - guestCount:", formData.guestCount, typeof formData.guestCount);
      console.log("  - reservationDate:", formData.reservationDate, typeof formData.reservationDate);
      console.log("Selected Data:");
      console.log("  - selectedOasis:", selectedOasis, typeof selectedOasis);
      console.log("  - selectedPackage:", selectedPackage?.name, typeof selectedPackage?.name);
      console.log("  - selectedSession:", selectedSession, typeof selectedSession);
      console.log("Calculated Data:");
      console.log("  - totalPrice:", totalPrice, typeof totalPrice);
      console.log("  - downpayment:", downpayment, typeof downpayment);
      console.log("  - paymentMethod:", formData.paymentMethod, typeof formData.paymentMethod);
      console.log("  - paymentType:", formData.paymentType, typeof formData.paymentType);
      console.log("===============================\n");

      // Validation
      if (!totalPrice || totalPrice === 0) {
        showAlert("Calculation Error", "Unable to calculate total price. Please ensure all fields are filled correctly.");
        setIsSubmitting(false);
        return;
      }

      if (downpayment === undefined || downpayment === null) {
        showAlert("Calculation Error", "Unable to calculate downpayment. Please check session data.");
        setIsSubmitting(false);
        return;
      }

      const bookingPayload = {
        customerName: formData.customerName,
        customerContact: formData.customerContact,
        customerEmail: formData.customerEmail,
        oasis: selectedOasis,
        package: selectedPackage.name,
        session: selectedSession,
        bookingDate: formData.reservationDate,
        pax: parseInt(formData.guestCount),
        totalPrice: totalPrice,
        downpayment: downpayment,
        paymentMethod: formData.paymentMethod,
        paymentType: formData.paymentType,
        paymentStatus: editingBooking?.paymentStatus || "Pending",
        status: editingBooking?.status || "Pending",
        specialRequests: formData.specialRequests,
        addons: Object.keys(selectedAddOns).length > 0 ? selectedAddOns : {},
      };

      console.log("📤 Booking Payload:", bookingPayload);
      console.log("   - totalPrice:", totalPrice, typeof totalPrice);
      console.log("   - downpayment:", downpayment, typeof downpayment);
      console.log("   - selectedSession:", selectedSession);
      console.log("   - selectedPackage:", selectedPackage);
      console.log("   - customerName:", formData.customerName);
      console.log("   - customerEmail:", formData.customerEmail);
      console.log("   - oasis:", selectedOasis);
      console.log("   - bookingDate:", formData.reservationDate);
      console.log("   - pax:", formData.guestCount);

      if (editingBooking) {
        await adminApi.updateBooking(editingBooking._id, bookingPayload);
      } else {
        await adminApi.createBooking(bookingPayload);
      }

      onBookingCreated();
    } catch (error) {
      console.error("Error saving booking:", error);
      const responseData = error.responseData || error.response?.data;
      const rawMessage = responseData?.message || error.message || error.toString();
      const isDuplicateBooking =
        responseData?.error === "DUPLICATE_BOOKING" ||
        rawMessage.includes("E11000") ||
        rawMessage.includes("no_double_booking");
      const errorMsg = isDuplicateBooking
        ? "This date and session is already booked. Please choose another date or session."
        : rawMessage;
      showAlert("Booking Not Saved", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = getTotalPrice();
  const downpayment = getDownpayment();

  return (
    <div className="admin-booking-modal">
      {/* Modal Header */}
      <div className="booking-modal-header">
        <div>
          <h2>{editingBooking ? "Edit Booking" : "Create New Booking"}</h2>
          <p className="modal-subtitle">Step {step} of 4</p>
        </div>
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Step Indicator */}
      <div className="step-indicator">
        <div className={`step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
          <span>1</span>
          <p>Customer Info</p>
        </div>
        <div className={`step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
          <span>2</span>
          <p>Date & Session</p>
        </div>
        <div className={`step ${step === 3 ? 'active' : step > 3 ? 'completed' : ''}`}>
          <span>3</span>
          <p>Payment</p>
        </div>
        <div className={`step ${step === 4 ? 'active' : ''}`}>
          <span>4</span>
          <p>Review</p>
        </div>
      </div>

      {/* Form Content - Scrollable */}
      <div className="form-content">
        {/* Step 1: Customer Info */}
        {step === 1 && (
          <div className="form-step">
            <h3>Customer Information</h3>
            <div className="form-group">
              <label className="required-label">
                Customer Name <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className={errors.customerName ? 'error' : ''}
              />
              {errors.customerName && <span className="error-text">{errors.customerName}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Contact Number <span className="required-asterisk">*</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="639XXXXXXXXX"
                value={formData.customerContact}
                onChange={(e) => {
                  const normalizedContact = normalizeContactNumber(e.target.value);
                  setFormData({ ...formData, customerContact: normalizedContact });
                }}
                className={errors.customerContact ? 'error' : ''}
              />
              {errors.customerContact && <span className="error-text">{errors.customerContact}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Email <span className="required-asterisk">*</span>
              </label>
              <input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                className={errors.customerEmail ? 'error' : ''}
              />
              {errors.customerEmail && <span className="error-text">{errors.customerEmail}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Location <span className="required-asterisk">*</span>
              </label>
              <select
                value={selectedOasis}
                onChange={(e) => {
                  setSelectedOasis(e.target.value);
                  setSelectedPackage(null);
                  setSelectedSession("");
                  fetchPackagesForOasis(e.target.value);
                }}
                className={errors.oasis ? 'error' : ''}
              >
                <option value="">Select Location</option>
                <option value="Oasis 1">Oasis 1</option>
                <option value="Oasis 2">Oasis 2</option>
              </select>
              {errors.oasis && <span className="error-text">{errors.oasis}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Package <span className="required-asterisk">*</span>
              </label>
              <select
                value={selectedPackage?.id || ""}
                onChange={(e) => {
                  const pkg = packages.find(p => (p.id || p.name) === e.target.value);
                  setSelectedPackage(pkg || null);
                  setSelectedSession("");
                }}
                disabled={!selectedOasis || loadingPackages}
                className={errors.package ? 'error' : ''}
              >
                <option value="">
                  {loadingPackages ? "Loading packages..." : "Select Package"}
                </option>
                {packages.map((pkg) => (
                  <option key={pkg.id || pkg.name} value={pkg.id || pkg.name}>
                    {pkg.name} {pkg.capacity ? `(${pkg.capacity})` : ""}
                  </option>
                ))}
              </select>
              {errors.package && <span className="error-text">{errors.package}</span>}
            </div>
          </div>
        )}

        {/* Step 2: Date & Session */}
        {step === 2 && (
          <div className="form-step">
            <h3>Booking Details</h3>
            <div className="form-group">
              <label className="required-label">
                Reservation Date <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                value={formData.reservationDate}
                onChange={(e) => setFormData({ ...formData, reservationDate: e.target.value })}
                min={getMinimumBookingDateValue()}
                className={errors.reservationDate ? 'error' : ''}
              />
              {errors.reservationDate && <span className="error-text">{errors.reservationDate}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Session <span className="required-asterisk">*</span>
              </label>
              <select
                value={selectedSession}
                onChange={(e) => setSelectedSession(e.target.value)}
                disabled={!currentPackage}
                className={errors.session ? 'error' : ''}
              >
                <option value="">Select Session</option>
                {currentPackage?.sessions && currentPackage.sessions.length > 0 ? (
                  currentPackage.sessions.map((session) => {
                    const inactive = shouldDisableSessionOption(session);
                    return (
                      <option 
                        key={session} 
                        value={session}
                        disabled={bookedSessions[session] || inactive}
                      >
                        {getSessionDisplay(session)}{bookedSessions[session] ? ' (BOOKED)' : inactive ? ' (INACTIVE)' : ''}
                      </option>
                    );
                  })
                ) : (
                  <option disabled>No sessions available for this package</option>
                )}
              </select>
              {currentPackage && (!currentPackage.sessions || currentPackage.sessions.length === 0) && (
                <small style={{color: 'red'}}>⚠️ Debug: No sessions found. Available sessions: {JSON.stringify(currentPackage.sessions)}</small>
              )}
              {errors.session && <span className="error-text">{errors.session}</span>}
            </div>

            <div className="form-group">
              <label className="required-label">
                Number of Guests <span className="required-asterisk">*</span>
              </label>
              <div className="pax-input-container">
                <button
                  type="button"
                  className="pax-btn pax-minus"
                  onClick={() => setFormData({ ...formData, guestCount: Math.max(1, formData.guestCount - 1) })}
                  disabled={formData.guestCount <= 1}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  value={formData.guestCount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '0') {
                      setFormData({ ...formData, guestCount: '' });
                    } else {
                      setFormData({ ...formData, guestCount: Math.max(1, parseInt(val) || 1) });
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setFormData({ ...formData, guestCount: 1 });
                    }
                  }}
                  className={`pax-input ${errors.guestCount ? 'error' : ''}`}
                />
                <button
                  type="button"
                  className="pax-btn pax-plus"
                  onClick={() => setFormData({ ...formData, guestCount: formData.guestCount + 1 })}
                >
                  +
                </button>
              </div>
              {errors.guestCount && <span className="error-text">{errors.guestCount}</span>}
              {currentPackage && (
                <small>Capacity: {getMinCapacityForPackage() > 0 ? `${getMinCapacityForPackage()}-` : ""}{getMaxCapacityForPackage()} pax</small>
              )}
            </div>

            <div className="form-group">
              <label>Special Requests</label>
              <textarea
                value={formData.specialRequests}
                onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                placeholder="Any special requests?"
              />
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="form-step">
            <h3>Payment Information</h3>
            <div className="price-summary">
              <div className="price-row">
                <span>Base Price:</span>
                <span>₱{calculatePrice().toLocaleString()}</span>
              </div>
              {calculateExtraGuestCharges() > 0 && (
                <div className="price-row">
                  <span>Extra Guests (₱150 each):</span>
                  <span>₱{calculateExtraGuestCharges().toLocaleString()}</span>
                </div>
              )}
              <div className="price-row total">
                <span>Total:</span>
                <span>₱{totalPrice.toLocaleString()}</span>
              </div>
              <div className="price-row">
                <span>Down Payment:</span>
                <span className="highlight">₱{downpayment.toLocaleString()}</span>
              </div>
            </div>

            {/* Add-ons Section */}
            <div className="addons-section">
              <h4>Add-ons (Optional)</h4>
              {loadingAddOns ? (
                <p className="loading-text">Loading add-ons...</p>
              ) : addOns.length > 0 ? (
                <div className="addons-grid">
                  {addOns.map((addon) => (
                    <label key={addon._id} className="addon-option">
                      <input
                        type="checkbox"
                        checked={!!selectedAddOns[addon.name]}
                        onChange={() => handleAddonToggle(addon)}
                      />
                      <div className="addon-info">
                        <span className="addon-name">{addon.name}</span>
                        <span className="addon-price">+ ₱{addon.price.toLocaleString()}</span>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="no-addons-text">No add-ons available</p>
              )}
              {Object.keys(selectedAddOns).length > 0 && (
                <div className="selected-addons-summary">
                  <small>
                    <strong>Selected Add-ons Total:</strong> ₱{calculateAddonsTotal().toLocaleString()}
                  </small>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="required-label">
                Payment Method <span className="required-asterisk">*</span>
              </label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              >
                <option value="GCash">GCash</option>
                <option value="Maya">Maya</option>
                <option value="GoTyme">GoTyme</option>
                <option value="SeaBank">SeaBank</option>
                <option value="Cash">Cash</option>
              </select>
            </div>

            <div className="form-group">
              <label>Initial Payment Type</label>
              <select
                value={formData.paymentType}
                disabled={!!editingBooking}
                onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
              >
                <option value="downpayment">Downpayment</option>
                <option value="fullpayment">Full Payment</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="form-step">
            <h3>Review Booking</h3>
            <div className="review-section">
              <div className="review-block">
                <h4>Customer Information</h4>
                <div className="review-item">
                  <span className="label">Name:</span>
                  <span className="value">{formData.customerName}</span>
                </div>
                <div className="review-item">
                  <span className="label">Contact:</span>
                  <span className="value">{formData.customerContact}</span>
                </div>
                <div className="review-item">
                  <span className="label">Email:</span>
                  <span className="value">{formData.customerEmail}</span>
                </div>
              </div>

              <div className="review-block">
                <h4>Booking Details</h4>
                <div className="review-item">
                  <span className="label">Location:</span>
                  <span className="value">{selectedOasis}</span>
                </div>
                <div className="review-item">
                  <span className="label">Package:</span>
                  <span className="value">{selectedPackage?.name}</span>
                </div>
                <div className="review-item">
                  <span className="label">Date:</span>
                  <span className="value">{new Date(formData.reservationDate).toLocaleDateString()}</span>
                </div>
                <div className="review-item">
                  <span className="label">Session:</span>
                  <span className="value">{getSessionDisplay(selectedSession)}</span>
                </div>
                <div className="review-item">
                  <span className="label">Guests:</span>
                  <span className="value">{formData.guestCount} pax</span>
                </div>
                {formData.specialRequests && (
                  <div className="review-item">
                    <span className="label">Special Requests:</span>
                    <span className="value">{formData.specialRequests}</span>
                  </div>
                )}
              </div>

              {Object.keys(selectedAddOns).length > 0 && (
                <div className="review-block">
                  <h4>Selected Add-ons</h4>
                  {Object.entries(selectedAddOns).map(([name, price]) => (
                    <div key={name} className="review-item">
                      <span className="label">{name}:</span>
                      <span className="value">+ ₱{price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="review-block">
                <h4>Payment & Status</h4>
                <div className="review-item">
                  <span className="label">Total Amount:</span>
                  <span className="value">₱{totalPrice.toLocaleString()}</span>
                </div>
                {calculateAddonsTotal() > 0 && (
                  <div className="review-item">
                    <span className="label">Add-ons Total:</span>
                    <span className="value">₱{calculateAddonsTotal().toLocaleString()}</span>
                  </div>
                )}
                <div className="review-item">
                  <span className="label">{formData.paymentType === "fullpayment" ? "Amount Due Now:" : "Down Payment:"}</span>
                  <span className="value">₱{(formData.paymentType === "fullpayment" ? totalPrice : downpayment).toLocaleString()}</span>
                </div>
                <div className="review-item">
                  <span className="label">Payment Method:</span>
                  <span className="value">{formData.paymentMethod}</span>
                </div>
                <div className="review-item">
                  <span className="label">Initial Payment Type:</span>
                  <span className="value">{formData.paymentType === "fullpayment" ? "Full Payment" : "Downpayment"}</span>
                </div>
                <div className="review-item status-auto-update">
                  <span className="label">Booking Status:</span>
                  <span className={`value booking-status-${(editingBooking?.status || "Pending").toLowerCase()}`}>
                    {editingBooking ? editingBooking.status : "Pending (payment verification required)"}
                  </span>
                </div>
                <div className="review-item">
                  <span className="label">Payment Status:</span>
                  <span className={`value payment-status-${(editingBooking?.paymentStatus || "Pending").toLowerCase()}`}>
                    {editingBooking?.paymentStatus || "Pending"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Footer - Navigation Buttons */}
      <div className="booking-modal-footer">
        <button
          className="btn-secondary"
          onClick={step === 1 ? onClose : handlePrevStep}
        >
          {step === 1 ? "Cancel" : "Back"}
        </button>
        {step < 4 && (
          <button
            className="btn-primary"
            onClick={handleNextStep}
          >
            Next
          </button>
        )}
        {step === 4 && (
          <button
            className="btn-success"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : editingBooking ? "Update Booking" : "Create Booking"}
          </button>
        )}
      </div>

      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <h2 style={{ margin: '0 0 12px', color: '#1e293b' }}>{alertModal.title}</h2>
            <p style={{ margin: '0 0 24px', color: '#64748b', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{alertModal.message}</p>
            <button
              onClick={() => setAlertModal({ ...alertModal, isOpen: false })}
              style={{
                width: '100%',
                padding: '10px',
                background: '#0284c7',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBookingForm;
